export const NON_INTERNAL_WORK_ITEM_COMMENT_TYPES = ['closing_details', 'external', 'total_finance'] as const;

export type NonInternalWorkItemCommentType = typeof NON_INTERNAL_WORK_ITEM_COMMENT_TYPES[number];
export type WorkItemCommentType = 'internal' | 'billing' | NonInternalWorkItemCommentType;

export interface WorkItemCommentTypeLike {
  is_internal?: boolean | null;
  comment_type?: string | null;
}

export interface CustomerFacingCommentSummary {
  id: string;
  user_name: string;
  comment_text: string;
  created_at: string;
  is_internal: boolean;
  comment_type?: WorkItemCommentType | null;
}

export function normalizeNonInternalWorkItemCommentType(value: unknown): NonInternalWorkItemCommentType | null {
  if (typeof value !== 'string') {
    return null;
  }

  const normalizedValue = value.trim().toLowerCase();
  return NON_INTERNAL_WORK_ITEM_COMMENT_TYPES.includes(normalizedValue as NonInternalWorkItemCommentType)
    ? normalizedValue as NonInternalWorkItemCommentType
    : null;
}

export function resolveWorkItemCommentType(comment: WorkItemCommentTypeLike): WorkItemCommentType {
  const rawType = typeof comment.comment_type === 'string' ? comment.comment_type.trim().toLowerCase() : null;

  // Billing and total-finance are explicit comment categories that should
  // be preserved regardless of the is_internal flag. The is_internal flag
  // then controls PDF visibility for total_finance comments.
  if (rawType === 'billing') {
    return 'billing';
  }

  if (rawType === 'total_finance') {
    return 'total_finance';
  }

  if (comment.is_internal) {
    return 'internal';
  }

  return normalizeNonInternalWorkItemCommentType(comment.comment_type) || 'closing_details';
}

export function getWorkItemCommentTypeForPersistence(
  commentType: unknown,
  isInternal?: boolean | null,
): WorkItemCommentType {
  const rawType = typeof commentType === 'string' ? commentType.trim().toLowerCase() : null;

  // Preserve explicit category types. For total_finance the is_internal flag
  // controls whether the comment is printed on PDFs, not the comment type.
  if (rawType === 'billing') {
    return 'billing';
  }

  if (rawType === 'total_finance') {
    return 'total_finance';
  }

  if (isInternal) {
    return 'internal';
  }

  return normalizeNonInternalWorkItemCommentType(commentType) || 'closing_details';
}

export function isClosingDetailsComment(comment: WorkItemCommentTypeLike): boolean {
  return resolveWorkItemCommentType(comment) === 'closing_details';
}

export function isExternalWorkItemComment(comment: WorkItemCommentTypeLike): boolean {
  return resolveWorkItemCommentType(comment) === 'external';
}

export function isInternalWorkItemComment(comment: WorkItemCommentTypeLike): boolean {
  return resolveWorkItemCommentType(comment) === 'internal';
}

export function getWorkItemCommentTypeLabel(commentType: WorkItemCommentType): string {
  switch (commentType) {
    case 'internal':
      return 'Internal';
    case 'billing':
      return 'Billing';
    case 'external':
      return 'External';
    case 'total_finance':
      return 'Total Finance';
    case 'closing_details':
    default:
      return 'Closing Details';
  }
}

export function isBillingWorkItemComment(comment: WorkItemCommentTypeLike): boolean {
  return resolveWorkItemCommentType(comment) === 'billing';
}

export function isTotalFinanceWorkItemComment(comment: WorkItemCommentTypeLike): boolean {
  return resolveWorkItemCommentType(comment) === 'total_finance';
}
